import { useState, useCallback, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from '@tanstack/react-router';
import { uniqueId } from '@utils/uniqueId';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, AlertModal } from '@components/ui';
import Modal from '@components/ui/Modal/Modal';
import ProfileActionsMenu from '@components/shared/ProfileActionsMenu/ProfileActionsMenu';
import type { ProfileAction } from '@components/shared/ProfileActionsMenu/ProfileActionsMenu';
import { Forbidden } from '@workpay/ds-shared/components/Forbidden/Forbidden';
import { isForbiddenError } from '@workpay/ds-shared/components/Forbidden/ForbiddenBoundary';
import { serializeFilters } from '@utils/tabHelpers';
import { DraggableTabs } from '@components/shared/DraggableTabs/DraggableTabs';
import type { TabItem } from '@components/shared/DraggableTabs/types';
import { getLegalEntityById, updateLegalEntity, deleteLegalEntity } from '@/services/legalEntityService';
import { useNotificationStore } from '@stores/notificationStore';
import { useAuthStore } from '@/stores/authStore';
import { useBreadcrumbOverride } from '@stores/breadcrumbStore';
import type { FormTemplateTab } from '@/types/formTemplate';
import type { UpdateLegalEntityPayload } from '@/types/legalEntity';
import { WorkpayApiError } from '@lib/apiEnvelope';
import { toDraggableTabItems, getRemovedTabDetails, toApiTabsFromResolved } from '@utils/tabHelpers';
import { useTranslation } from '@/hooks/useTranslation';
import ProfileCard from '@features/tenant-profile/ProfileCard';
import type { ProfileField } from '@features/tenant-profile/ProfileCard';
import TabFormsContent from '@features/tenant-profile/TabFormsContent';
import EntityEditForm from './EntityEditForm';
import type { ResolvedTab, ResolvedFormEntry } from '@features/tenant-profile/types';
import { AuditTrailModal } from '@components/shared/AuditTrailModal';
import { AUDIT_RESOURCE } from '@lib/auditResourceTypes';
import styles from './EntityProfileView.module.scss';

// ============================================
// HELPERS
// ============================================

function buildProfileFields(
  entity: Awaited<ReturnType<typeof getLegalEntityById>>,
  t: (key: string, fallback: string) => string,
): ProfileField[] {
  const entityTypeLabel = entity.entityType === 'WORKPAY_OWNED' ? t('K_OPTION_WORKPAY_OWNED', 'Workpay Owned')
    : entity.entityType === 'PARTNER' ? t('K_OPTION_PARTNER', 'Partner')
    : '-';

  const employmentTypesLabel = entity.employmentTypes && entity.employmentTypes.length > 0
    ? entity.employmentTypes.map((et) => et.name).join(', ')
    : '-';

  return [
    { label: t('K_LABEL_ENTITY_NAME', 'Entity Name'), value: entity.name || '-' },
    { label: t('K_LABEL_COUNTRY', 'Country'), value: entity.country?.name || '-' },
    { label: t('K_LABEL_ENTITY_TYPE', 'Entity Type'), value: entityTypeLabel },
    { label: t('K_LABEL_EMPLOYMENT_TYPES', 'Employment Types'), value: employmentTypesLabel },
    { label: t('K_LABEL_EFFECTIVE_FROM', 'Effective From'), value: entity.effectiveFrom ? new Date(entity.effectiveFrom).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) : '-' },
    { label: t('K_LABEL_STATUS', 'Status'), value: entity.status || '-' },
  ];
}

function toResolvedTabs(apiTabs: FormTemplateTab[]): ResolvedTab[] {
  return apiTabs.map((tab) => ({
    _tabClientId: uniqueId('tab'),
    name: tab.name,
    description: tab.description ?? '',
    order: tab.order,
    forms: (tab.forms ?? []).map((form): ResolvedFormEntry => ({
      formConfigId: form.formConfigId,
      formName: form.formName ?? form.formConfigId,
      accessLevel: form.accessLevel,
      isDefault: form.attributes.isDefault,
    })),
  }));
}

// ============================================
// COMPONENT
// ============================================

interface EntityProfileViewProps {
  entityId: string;
  onBack?: () => void;
  /** When true on first mount, opens the edit modal automatically (used by `?edit=true` from list pages). */
  initialEdit?: boolean;
}

function EntityProfileView({ entityId, onBack, initialEdit = false }: EntityProfileViewProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const addNotification = useNotificationStore((s) => s.addNotification);
  const hasPermission = useAuthStore((s) => s.hasPermission);
  const pathname = useLocation({ select: (l) => l.pathname });
  const setOverride = useBreadcrumbOverride((s) => s.setOverride);
  const [isEditing, setIsEditing] = useState(initialEdit);
  const [editPending, setEditPending] = useState(false);
  const [showDeactivateConfirm, setShowDeactivateConfirm] = useState(false);
  const [auditOpen, setAuditOpen] = useState(false);

  const handleEditStart = useCallback(() => setIsEditing(true), []);
  const handleEditClose = useCallback(() => {
    setIsEditing(false);
    // Scrub `?edit=true` on close so refresh doesn't reopen the modal.
    if (initialEdit) {
      void navigate({
        to: '/w/entities/profile/$entityId',
        params: { entityId },
        search: {},
        replace: true,
      });
    }
  }, [initialEdit, navigate, entityId]);
  const handleEditCancel = handleEditClose;
  const handleEditSaved = handleEditClose;

  const deactivateMutation = useMutation({
    mutationFn: () => deleteLegalEntity(entityId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['legalEntity', entityId] });
      void queryClient.invalidateQueries({ queryKey: ['legalEntities'] });
      setShowDeactivateConfirm(false);
    },
    onError: (error) => {
      const e = error as WorkpayApiError;
      addNotification({ variant: 'critical', title: t(e.description, e.description) });
      setShowDeactivateConfirm(false);
    },
  });

  // --- Entity data ---
  const {
    data: entityData,
    isLoading,
    isError,
    error: entityErrorObj,
  } = useQuery({
    queryKey: ['legalEntity', entityId],
    queryFn: () => getLegalEntityById(entityId),
  });
  const entityForbidden = isForbiddenError(entityErrorObj);

  // Dynamic breadcrumb: show entity name
  useEffect(() => {
    if (entityData?.name) {
      setOverride(pathname, entityData.name);
    }
    return () => setOverride(pathname, null);
  }, [entityData?.name, pathname, setOverride]);

  // Edit modal renders only when entity is loaded and not INACTIVE.
  // Computed at the boundary so `?edit=true` deep-link on an INACTIVE record
  // never flashes the modal open between mount and effect.
  const editModalOpen = isEditing && !!entityData && entityData.status !== 'INACTIVE';

  // --- Local tab state ---
  const [tabs, setTabs] = useState<ResolvedTab[]>([]);
  const [activeTabId, setActiveTabId] = useState('');
  const [savedTabsJson, setSavedTabsJson] = useState('');
  const [initialized, setInitialized] = useState(false);
  const [removedTabIds, setRemovedTabIds] = useState<Set<string>>(new Set());
  const [showSaveConfirm, setShowSaveConfirm] = useState(false);

  // Sync resolved data into local state once
  if (entityData && entityData.tabs && !initialized) {
    const clientTabs = toResolvedTabs(entityData.tabs);
    setTabs(clientTabs);
    setSavedTabsJson(JSON.stringify(toApiTabsFromResolved(clientTabs)));
    if (clientTabs[0]) {
      setActiveTabId(clientTabs[0]._tabClientId);
    }
    setInitialized(true);
  }
  if (entityData && !entityData.tabs && !initialized) {
    setInitialized(true);
  }

  const liveTabs = useMemo(
    () => tabs.filter((t) => !removedTabIds.has(t._tabClientId)),
    [tabs, removedTabIds],
  );

  const isDirty = useMemo(
    () => liveTabs.length > 0
      ? JSON.stringify(toApiTabsFromResolved(liveTabs)) !== savedTabsJson
      : removedTabIds.size > 0,
    [liveTabs, savedTabsJson, removedTabIds.size],
  );

  const removedTabDetails = useMemo(
    () => getRemovedTabDetails(tabs, removedTabIds),
    [tabs, removedTabIds],
  );

  const activeTab = tabs.find((t) => t._tabClientId === activeTabId);
  const draggableItems = useMemo(() => toDraggableTabItems(tabs), [tabs]);

  // --- Save mutation ---
  const saveMutation = useMutation({
    mutationFn: (data: UpdateLegalEntityPayload) => updateLegalEntity(entityId, data),
  });

  // --- Handlers ---
  const handleTabClick = useCallback((tab: TabItem) => {
    setActiveTabId(tab.id);
  }, []);

  const handleReorder = useCallback((reorderedItems: TabItem[]) => {
    setTabs((prev) => {
      const tabMap = new Map(prev.map((t) => [t._tabClientId, t]));
      return reorderedItems
        .map((item) => tabMap.get(item.id))
        .filter((t): t is ResolvedTab => t !== undefined);
    });
  }, []);

  const handleRemoveTab = useCallback((tab: TabItem) => {
    setRemovedTabIds((prev) => new Set(prev).add(tab.id));
    if (tab.id === activeTabId) {
      const currentIndex = tabs.findIndex((t) => t._tabClientId === tab.id);
      const remaining = tabs.filter(
        (t, i) => !removedTabIds.has(t._tabClientId) && t._tabClientId !== tab.id && i < currentIndex,
      );
      const fallback = remaining[remaining.length - 1]
        ?? tabs.find((t) => !removedTabIds.has(t._tabClientId) && t._tabClientId !== tab.id);
      setActiveTabId(fallback?._tabClientId ?? '');
    }
  }, [activeTabId, tabs, removedTabIds]);

  const handleUndoRemoveTab = useCallback((tab: TabItem) => {
    setRemovedTabIds((prev) => {
      const next = new Set(prev);
      next.delete(tab.id);
      return next;
    });
  }, []);

  const performSave = useCallback(() => {
    const tabsToSave = tabs.filter((t) => !removedTabIds.has(t._tabClientId));
    saveMutation.mutate(
      { tabs: toApiTabsFromResolved(tabsToSave) },
      {
        onSuccess: () => {
          void queryClient.invalidateQueries({ queryKey: ['legalEntity', entityId] });
          setTabs(tabsToSave);
          setSavedTabsJson(JSON.stringify(toApiTabsFromResolved(tabsToSave)));
          setRemovedTabIds(new Set());
          setShowSaveConfirm(false);
        },
        onError: (error) => {
          const e = error as WorkpayApiError;
          addNotification({ variant: 'critical', title: t(e.description, e.description) });
        },
      },
    );
  }, [tabs, removedTabIds, saveMutation, queryClient, entityId]);

  const handleSaveClick = useCallback(() => {
    if (removedTabIds.size > 0) {
      setShowSaveConfirm(true);
    } else {
      performSave();
    }
  }, [removedTabIds.size, performSave]);

  const handleCancelSave = useCallback(() => {
    setShowSaveConfirm(false);
  }, []);

  // --- Profile fields ---
  const profileFields = useMemo<ProfileField[]>(
    () => (entityData ? buildProfileFields(entityData, t) : []),
    [entityData, t],
  );

  // --- Loading state ---
  if (isLoading) {
    return (
      <div className={styles.page}>
        {onBack && (
          <button type="button" className={styles.backLink} onClick={onBack}>
            &larr; Back
          </button>
        )}
        <ProfileCard name="" fields={[]} loading />
        <div className={styles.loadingState}>{t('K_LOADING_ENTITY_PROFILE', 'Loading entity profile...')}</div>
      </div>
    );
  }

  // --- Forbidden (page-level — detail route can't render anything without the entity) ---
  if (entityForbidden) {
    return (
      <div className={styles.page}>
        <Forbidden
          variant="page"
          onAction={onBack}
          actionLabel={onBack ? t('K_BUTTON_GO_BACK', 'Go Back') : undefined}
          hideAction={!onBack}
        />
      </div>
    );
  }

  // --- Error state ---
  if (isError || !entityData) {
    return (
      <div className={styles.page}>
        {onBack && (
          <button type="button" className={styles.backLink} onClick={onBack}>
            &larr; Back
          </button>
        )}
        <div className={styles.errorState}>
          <p>{t('K_FAILED_LOAD_ENTITY_PROFILE', 'Failed to load entity profile.')}</p>
          <Button variant="outline" size="sm" onClick={onBack}>
            {t('K_BUTTON_GO_BACK', 'Go Back')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      {onBack && (
        <button type="button" className={styles.backLink} onClick={onBack}>
          &larr; Back
        </button>
      )}

      {/* Profile Card — LE has no own profile_image_path per BE V26 migration.
          Renders the parent org's logo only after we wire a parent-org fetch
          (legalEntity.organizationId → organization.profileImageUrl); not in
          this pass. Falls back to plain initials avatar today. */}
      <ProfileCard
        name={entityData.name || t('K_LABEL_ENTITY', 'Entity')}
        fields={profileFields}
        avatarColor="blue"
        action={
          <div className={styles.actionGroup}>
            <span
              className={`${styles.statusBadge} ${
                entityData.status === 'ACTIVE' ? styles.statusBadgeActive : styles.statusBadgeInactive
              }`}
            >
              {entityData.status === 'ACTIVE'
                ? t('K_STATUS_ACTIVE', 'Active')
                : t('K_STATUS_INACTIVE', 'Inactive')}
            </span>
            <ProfileActionsMenu
              actions={(() => {
              const items: ProfileAction[] = [];
              if (hasPermission('ENTITIES_UPDATE') && entityData.status !== 'INACTIVE') {
                items.push({ id: 'edit', label: t('K_EDIT', 'Edit'), onClick: handleEditStart });
              }
              items.push({
                id: 'viewEmployees',
                label: t('K_BUTTON_VIEW_ALL_EMPLOYEES', 'View All Employees'),
                onClick: () => {
                  void navigate({
                    to: '/w/employees',
                    search: {
                      tab: 'ALL',
                      filters: serializeFilters({ legalEntityId: entityId }),
                    },
                  });
                },
              });
              if (hasPermission('ENTITIES_DELETE') && entityData.status === 'ACTIVE') {
                items.push({ id: 'deactivate', label: t('K_DEACTIVATE', 'Deactivate'), onClick: () => setShowDeactivateConfirm(true) });
              }
              if (hasPermission('AUDIT_READ')) {
                items.push({ id: 'audit', label: t('K_AUDIT_TITLE', 'Audit trail'), onClick: () => setAuditOpen(true) });
              }
              return items;
            })()}
            />
          </div>
        }
      />

      {/* Edit Modal */}
      <Modal
        isOpen={editModalOpen}
        onClose={handleEditCancel}
        title={t('K_MODAL_EDIT_ENTITY', 'Edit Entity')}
        size="md"
        footer={
          <div className={styles.editModalFooter}>
            <Button variant="outline" size="sm" onClick={handleEditCancel} disabled={editPending}>
              {t('K_CANCEL', 'Cancel')}
            </Button>
            <Button variant="filled" size="sm" type="submit" form="entity-edit-form" disabled={editPending}>
              {editPending ? t('K_BUTTON_SAVING', 'Saving...') : t('K_SAVE', 'Save')}
            </Button>
          </div>
        }
      >
        {entityData && (
          <EntityEditForm
            entityData={entityData}
            formId="entity-edit-form"
            onSaved={handleEditSaved}
            onPendingChange={setEditPending}
          />
        )}
      </Modal>

      {/* Toolbar — Save button when tab reorder/removal is dirty */}
      {isDirty && hasPermission('ENTITIES_UPDATE') && entityData.status !== 'INACTIVE' && (
        <div className={styles.toolbar}>
          <Button
            variant="filled"
            size="sm"
            onClick={handleSaveClick}
            disabled={saveMutation.isPending}
          >
            {saveMutation.isPending ? t('K_BUTTON_SAVING', 'Saving...') : t('K_SAVE', 'Save')}
          </Button>
        </div>
      )}

      {/* Tabs */}
      {tabs.length > 0 && (
        <>
          <DraggableTabs
            tabs={draggableItems}
            activeTabId={activeTabId}
            onTabClick={handleTabClick}
            onReorder={handleReorder}
            draggable={hasPermission('ENTITIES_UPDATE') && entityData.status !== 'INACTIVE'}
            removable={hasPermission('ENTITIES_UPDATE') && entityData.status !== 'INACTIVE'}
            removedTabIds={removedTabIds}
            onRemove={handleRemoveTab}
            onUndoRemove={handleUndoRemoveTab}
          />

          {/* Tab content */}
          <div className={styles.tabContent}>
            {activeTab && !removedTabIds.has(activeTab._tabClientId) ? (
              <TabFormsContent
                forms={activeTab.forms}
                mode={{ kind: 'admin', tenantId: entityId }}
                objectType="LEGAL_ENTITY"
                objectId={entityId}
              />
            ) : (
              <p className={styles.tabContentPlaceholder}>
                {t('K_SELECT_TAB_PLACEHOLDER', 'Select a tab to view its forms.')}
              </p>
            )}
          </div>
        </>
      )}


      <AuditTrailModal
        open={auditOpen}
        onClose={() => setAuditOpen(false)}
        filter={{ resourceType: AUDIT_RESOURCE.LEGAL_ENTITY, resourceId: entityId }}
        subject={entityData.name || undefined}
      />

      {/* Deactivate entity confirmation modal */}
      <AlertModal
        variant="danger"
        title={t('K_MODAL_DEACTIVATE_ENTITY', 'Deactivate Legal Entity')}
        description={t('K_CONFIRM_DEACTIVATE_ENTITY', 'Are you sure you want to deactivate "{name}"? The legal entity will no longer be active.').replace('{name}', entityData.name || '')}
        isOpen={showDeactivateConfirm}
        onClose={() => setShowDeactivateConfirm(false)}
        primaryLabel={t('K_DEACTIVATE', 'Deactivate')}
        onPrimaryClick={() => deactivateMutation.mutate()}
        loading={deactivateMutation.isPending}
      />

      {/* Save confirmation modal */}
      <Modal
        isOpen={showSaveConfirm}
        onClose={handleCancelSave}
        title={t('K_MODAL_CONFIRM_CHANGES', 'Confirm Changes')}
        size="sm"
        footer={
          <div className={styles.confirmFooter}>
            <Button variant="outline" size="sm" onClick={handleCancelSave}>
              {t('K_CANCEL', 'Cancel')}
            </Button>
            <Button variant="filled" size="sm" onClick={performSave} disabled={saveMutation.isPending}>
              {saveMutation.isPending ? t('K_BUTTON_SAVING', 'Saving...') : t('K_CONFIRM', 'Confirm')}
            </Button>
          </div>
        }
      >
        <div className={styles.confirmBody}>
          <p>{t('K_MESSAGE_TABS_PERMANENTLY_REMOVED', 'The following tabs will be permanently removed:')}</p>
          <ul className={styles.confirmList}>
            {removedTabDetails.map((tab, i) => (
              <li key={i}>
                {tab.name}{tab.formNames && ` - ${tab.formNames}`}
              </li>
            ))}
          </ul>
        </div>
      </Modal>
    </div>
  );
}

export default EntityProfileView;
